import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { DraftConfiguration, Point, Size } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { clamp } from '@/utils/math/clamp';
import { isEqualPoints } from '@/utils/math/isEqualPoints';
import { round } from '@/utils/math/round';
import { PinchHandler } from '@/hooks/usePinch';
import { STICKER_SIZE_ROUTE } from '@/hooks/useStickerSize';
import { convertAndScale } from '@/model/configuration/convertAndScale';
import { getMaxScale } from '@/model/configuration/getMaxScale';
import { isDraftDesignConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { rotatedSize } from '@/model/configuration/rotatedSize';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';
import { getIsStickerByPrintType } from '@/model/printType/getIsStickerByPrintType';
import { isEnlargeable } from '@/model/printType/isEnlargeable';
import { isShrinkable } from '@/model/printType/isShrinkable';
import { useMultiSelectContext } from '@/components/draft/MultiSelectContext';
import { useTransformChangedCallback } from '@/components/draft/TransformChangedContext';
import { useDraftDispatch } from '../DraftContext';
import { removeConfigurationAction } from '../draftState/actions/removeConfigurationAction';
import {
  Transformation,
  setTransformationAction,
} from '../draftState/actions/setTransformationAction';
import { setWarningAction } from '../draftState/actions/setWarningAction';
import { DraftWarning } from '../draftState/types';
import { getLocalizedSize } from '../utils/getLocalizedSize';
import { warnOnMinimumScale } from '../utils/warnOnMinimumScale';
import { useKeyboardHandler } from './useKeyboardHandler';
import { useSnapLines } from './useSnapLines';

export const normalizeAngle = (angle: number) => {
  let differenceTo45 = angle % 45;
  if (differenceTo45 >= 40) {
    differenceTo45 -= 45;
  }
  if (Math.abs(differenceTo45) <= 5) {
    angle = Math.round(angle - differenceTo45);
  }

  if (angle >= 360) {
    angle -= 360;
  }

  return angle;
};

interface ConfigurationTransformState {
  scale: number;
  offset: Point;
  rotation: number;
  infoText: string;
  showMaxSizeHint: boolean;
}

export interface ConfigurationTransformPayload {
  scale: number;
  offset: Point;
  rotation: number;
  infoText?: string;
  showMaxSizeHint?: boolean;
}

interface ConfigurationTransformAction {
  type: ConfigurationTransformActionTypes.transform;
  payload: ConfigurationTransformPayload;
}

enum ConfigurationTransformActionTypes {
  transform,
}

const reducer = (
  state: ConfigurationTransformState,
  { type, payload }: ConfigurationTransformAction
) => (type === ConfigurationTransformActionTypes.transform ? { ...state, ...payload } : state);

const resetTransformState = (configuration: DraftConfiguration) => ({
  scale: configuration.scale,
  offset: configuration.offset,
  rotation: configuration.rotation,
  infoText: '',
  showMaxSizeHint: false,
});

const canPinchConfiguration = (configuration: DraftConfiguration) => {
  if (isDraftDesignConfiguration(configuration)) {
    const restrictions = configuration.design.restrictions;
    return restrictions.allowScale && restrictions.allowRotate;
  }

  return true;
};

export function useConfigurationTransform(
  configuration: DraftConfiguration,
  ref: MutableRefObject<SVGGraphicsElement | null>,
  isSelected: boolean,
  isPixelConfiguration: boolean,
  minimumScale?: number
) {
  const {
    cid,
    printArea,
    printType,
    offset: configurationOffset,
    rotation: configurationRotation,
    scale: configurationScale,
    size: configurationSize,
  } = configuration;
  const onTransformChanged = useTransformChangedCallback();
  const [snapLinesActive, setSnapLinesActive] = useState(true);

  const [transformState, dispatchTransform] = useReducer(
    reducer,
    configuration,
    resetTransformState
  );

  const isStickerPanelOpen =
    Boolean(useMatch(STICKER_SIZE_ROUTE)) && !getIsStickerByPrintType(printType.id);

  const navigate = useNavigate();

  // prevents an invalid intermediate state with active transformState and new configuration settings
  //  until the reset got dispatched in the useEffect
  const lastConfigurationRef = useRef(configuration);
  const currentTransform =
    lastConfigurationRef.current === configuration
      ? transformState
      : resetTransformState(configuration);

  const setTransform = useCallback(
    (payload: ConfigurationTransformPayload) =>
      dispatchTransform({
        type: ConfigurationTransformActionTypes.transform,
        payload,
      }),
    []
  );

  useEffect(() => {
    if (lastConfigurationRef.current !== configuration) {
      lastConfigurationRef.current = configuration;
      setTransform(resetTransformState(configuration));
    }
  }, [configuration, setTransform]);

  const setTransformAndNotify = useCallback(
    (cid: number, payload: ConfigurationTransformPayload, size: Size) => {
      setTransform(payload);
      onTransformChanged?.({
        cid,
        offset: payload.offset,
        rotation: payload.rotation,
        size,
      });
    },
    [setTransform, onTransformChanged]
  );

  const { scale, offset, rotation, infoText, showMaxSizeHint } = currentTransform;

  const currentConfigurationSize = useMemo(
    () => scaleSize(configurationSize, configurationScale),
    [configurationSize, configurationScale]
  );
  const maxScale = useMemo(
    () => getMaxScale(configurationSize, rotation, printArea, printType, isPixelConfiguration),
    [configurationSize, rotation, printType, printArea, isPixelConfiguration]
  );

  // clone the offset into a ref, so we can move and save the offset
  // in the keyboard handlers without recreating the handlers every time
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const moveOffsetBy = useCallback(
    ({ x, y }: Point) => {
      const { x: currentX, y: currentY } = offsetRef.current;
      const offsetX = currentX + x;
      const offsetY = currentY + y;
      setTransformAndNotify(
        cid,
        {
          offset: { x: offsetX, y: offsetY },
          rotation: configurationRotation,
          scale: configurationScale,
          infoText: `X: ${getLocalizedSize(offsetX)} , Y: ${getLocalizedSize(offsetY)}`,
        },
        currentConfigurationSize
      );
    },
    [
      setTransformAndNotify,
      cid,
      configurationRotation,
      configurationScale,
      currentConfigurationSize,
    ]
  );

  const dispatch = useDraftDispatch();
  const saveTransform = useCallback(
    (transformation: Transformation) => {
      dispatch(setTransformationAction(configuration, transformation));
      onTransformChanged?.({ last: true });
    },
    [dispatch, configuration, onTransformChanged]
  );
  const saveOffset = useCallback(() => {
    saveTransform({ offset: offsetRef.current });
  }, [saveTransform, offsetRef]);
  const removeConfiguration = useCallback(() => {
    trackFeature('delete-configuration');
    dispatch(removeConfigurationAction(configuration));
  }, [dispatch, configuration]);

  const { configurationIds } = useMultiSelectContext();
  const isKeyboardHandlerActive =
    configurationIds.length > 0 ? configurationIds.includes(cid) : isSelected;
  useKeyboardHandler(isKeyboardHandlerActive, moveOffsetBy, saveOffset, removeConfiguration);
  const [isSnapTarget, snapLines, matchSnapLines, resetSnapLines] = useSnapLines(
    printArea,
    configuration,
    configurationSize && convertAndScale(configurationSize, configuration.scale, printType)
  );

  const getScaleAndOffsetForTargetScale = (
    targetScale: number,
    maxScale: number,
    currentSize: Size
  ) => {
    let newScale = targetScale;
    if (isShrinkable(printType)) {
      newScale = Math.min(targetScale, maxScale);
    } else if (isEnlargeable(printType)) {
      if (minimumScale) {
        newScale = clamp(targetScale, maxScale, minimumScale);
        warnOnMinimumScale(targetScale, newScale, dispatch);
      }
    } else {
      newScale = scale;
    }

    const newSize = convertAndScale(configurationSize, newScale, printType);
    return {
      newSize,
      scale: newScale,
      offset: {
        x: configurationOffset.x + (currentSize.width - newSize.width) / 2,
        y: configurationOffset.y + (currentSize.height - newSize.height) / 2,
      },
    };
  };

  const onMove = (delta: Point, first: boolean, last: boolean, dragging: boolean | undefined) => {
    if (dragging && (delta.x !== 0 || delta.y !== 0)) {
      const factor = ref.current?.ownerSVGElement?.getScreenCTM()?.inverse().a;
      if (factor) {
        trackFeature('move-configuration');
        const newOffset = matchSnapLines(delta, rotation, factor, snapLinesActive);
        setTransformAndNotify(
          configuration.cid,
          {
            offset: newOffset,
            rotation: configurationRotation,
            scale: configurationScale,
            infoText: `X: ${getLocalizedSize(newOffset.x)} , Y: ${getLocalizedSize(newOffset.y)}`,
          },
          currentConfigurationSize
        );
      }
    }

    if (last) {
      resetSnapLines();

      if (isEqualPoints(offset, configurationOffset)) {
        setTransform(resetTransformState(configuration));
      } else {
        saveTransform({ offset });
      }
    }
  };

  const onSetSnapLineActive = () => {
    trackFeature('deactivate-snapline-configuration');
    setSnapLinesActive((currentSnapLineActive) => !currentSnapLineActive);
  };

  const onRotate = (
    rotationChange: number,
    first: boolean,
    last: boolean,
    dragging: boolean | undefined
  ) => {
    if (dragging) {
      trackFeature('rotate-configuration');

      const {
        scale: newScale,
        offset: adjustedOffset,
        newSize,
      } = getScaleAndOffsetForTargetScale(
        clamp(scale, maxScale, configuration.scale),
        maxScale,
        currentConfigurationSize
      );

      const newRotation = normalizeAngle(rotationChange + configuration.rotation);

      setTransformAndNotify(
        configuration.cid,
        {
          scale: newScale,
          offset: adjustedOffset,
          rotation: newRotation,
          infoText: `${Math.round(newRotation)}°`,
        },
        newSize
      );
    }

    if (last) {
      saveTransform({ scale, offset, rotation });
    }
  };

  const onScale = (
    targetScale: number,
    first: boolean,
    last: boolean,
    dragging: boolean | undefined
  ) => {
    if (isStickerPanelOpen) {
      navigate('/');
    }
    if (dragging) {
      trackFeature('resize-configuration');

      const { scale: adjustedScale, offset: adjustedOffset } = getScaleAndOffsetForTargetScale(
        targetScale,
        maxScale,
        currentConfigurationSize
      );

      const printAreaSize = getPrintAreaSize(printArea);
      const newSize = convertAndScale(configurationSize, scale, printType);
      const newRotatedSize = rotatedSize(rotation, newSize);

      const isPrintAreaRestrictingSize =
        Math.abs(round(newRotatedSize.width) - round(printAreaSize.width - 1)) < 1 ||
        Math.abs(round(newRotatedSize.height) - round(printAreaSize.height - 1)) < 1;

      const isPrintTypeRestrictingSize =
        Math.abs(round(newRotatedSize.width) - round(printType.size.width - 1)) < 1 ||
        Math.abs(round(newRotatedSize.height) - round(printType.size.height - 1)) < 1;

      if (!isPrintAreaRestrictingSize && scale === maxScale) {
        if (isPrintTypeRestrictingSize) {
          dispatch(setWarningAction(String(configuration.cid), DraftWarning.PrintAreaBound));
        } else if (!isPrintAreaRestrictingSize && isDraftDesignConfiguration(configuration)) {
          dispatch(setWarningAction(String(configuration.cid), DraftWarning.DPIBound));
        }
      }

      setTransformAndNotify(
        configuration.cid,
        {
          scale: adjustedScale,
          offset: adjustedOffset,
          rotation: configuration.rotation,
          infoText: `${getLocalizedSize(newSize.width)} x ${getLocalizedSize(newSize.height)}`,
          showMaxSizeHint: !last,
        },
        newSize
      );
    }

    if (last) {
      saveTransform({ scale, offset });
    }
  };

  const onPinch: PinchHandler | undefined = canPinchConfiguration(configuration)
    ? (pinchScale, pinchRotation, { last, pinching }) => {
        if (pinching) {
          trackFeature('pinch-configuration');

          const {
            scale: adjustedScale,
            offset: adjustedOffset,
            newSize,
          } = getScaleAndOffsetForTargetScale(
            Math.max(pinchScale, 0.01) * configuration.scale,
            maxScale,
            currentConfigurationSize
          );

          const newRotation = normalizeAngle(pinchRotation + configuration.rotation);

          setTransformAndNotify(
            configuration.cid,
            {
              scale: adjustedScale,
              offset: adjustedOffset,
              rotation: newRotation,
              infoText: '',
              showMaxSizeHint: true,
            },
            newSize
          );
        }

        if (last) {
          saveTransform({
            scale,
            offset,
            rotation,
          });
        }
      }
    : undefined;

  return {
    scale,
    maxScale,
    offset,
    rotation,
    infoText,
    isSnapTarget,
    onDelete: removeConfiguration,
    onMove,
    onPinch,
    onRotate,
    onScale,
    saveTransform,
    setTransformAndNotify,
    showMaxSizeHint,
    snapLines,
    onSetSnapLineActive,
    snapLinesActive,
  };
}
